import { useLayoutEffect, useRef, useState } from 'react';
import { Avatar } from '../ui/Avatar';
import { readableTextColor } from '../../lib/colorContrast';
import {
  placeTaskPreview,
  previewCountLine,
  type TaskPreviewModel,
} from './taskPreview';
import './taskPreview.css';

function avatarName(name: string): string {
  return name.replace(/^Me\s+/i, '').trim() || name;
}

export function TaskPreviewCard({
  model,
  anchor,
  shown,
}: {
  model: TaskPreviewModel;
  anchor: DOMRect;
  shown: boolean;
}) {
  const ref = useRef<HTMLDivElement>(null);
  const [pos, setPos] = useState(() =>
    placeTaskPreview(anchor, { width: 320, height: 180 }, viewport()),
  );

  useLayoutEffect(() => {
    const node = ref.current;
    const width = node?.offsetWidth || 320;
    const height = node?.offsetHeight || 180;
    setPos(placeTaskPreview(anchor, { width, height }, viewport()));
  }, [anchor, model]);

  const counts = previewCountLine(model.commentCount, model.attachmentCount);
  const hasWhen = model.due != null || model.relance != null;
  const className = [
    'tp-preview',
    shown ? 'is-on' : '',
    model.status === 'late' ? 'is-late' : '',
  ].filter(Boolean).join(' ');

  return (
    <div
      ref={ref}
      className={className}
      style={{ left: pos.left, top: pos.top }}
      role="tooltip"
    >
      <div className="tp-head">
        <div className="tp-type">Tâche</div>
        <span className={`tp-pill ${model.status}`}>{model.statusLabel}</span>
      </div>
      <div className="tp-body">
        {model.dossierTitle && <div className="tp-dossier">{model.dossierTitle}</div>}
        <div className={`tp-title${model.titleIsHeading ? ' as-heading' : ''}${model.status === 'done' ? ' done' : ''}`}>
          {model.title}
        </div>
        <div className="tp-gold" />
        {model.description && <p className="tp-excerpt">{model.description}</p>}
        {hasWhen && (
          <div className={`tp-when${model.due ? '' : ' no-date'}`}>
            {model.due && (
              <div className={`tp-date${model.status === 'late' ? ' late' : ''}`}>
                <b>{model.due.day}</b>
                <span>{model.due.month}</span>
              </div>
            )}
            <div className="tp-when-copy">
              {model.due && (
                <div>
                  Échéance
                  {model.hour && <span className="tp-hour"> · {model.hour}</span>}
                </div>
              )}
              {model.relance && <span className="tp-relance">Relance {model.relance}</span>}
            </div>
          </div>
        )}
        {model.people.length > 0 && (
          <div className="tp-people">
            <span className="tp-avatars">
              {model.people.map((person) => (
                <Avatar
                  key={person.id}
                  name={avatarName(person.name)}
                  src={person.avatarUrl}
                  size="xs"
                />
              ))}
            </span>
            <em className="tp-names">{model.people.map((p) => p.name).join(', ')}</em>
          </div>
        )}
        {(model.priority || model.sectionName) && (
          <div className="tp-facts">
            {model.priority && (
              <span className={`tp-fact${model.priority.tone === 'mid' ? '' : ` ${model.priority.tone}`}`}>
                {model.priority.label}
              </span>
            )}
            {model.sectionName && <span className="tp-fact section">{model.sectionName}</span>}
          </div>
        )}
        {model.tags.length > 0 && (
          <div className="tp-tags">
            {model.tags.map((tag) => (
              <span
                key={tag.id}
                className="tp-tag"
                style={{ backgroundColor: tag.color, color: readableTextColor(tag.color) }}
              >
                {tag.name}
              </span>
            ))}
          </div>
        )}
        {counts && <div className="tp-counts">{counts}</div>}
      </div>
    </div>
  );
}

function viewport() {
  return { width: window.innerWidth, height: window.innerHeight };
}
